import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';

const TIPOS = ['vacaciones', 'baja', 'permiso', 'otro'];
const TIPO_COLOR = {
  vacaciones: { bg: '#E6F1FB', color: '#185FA5' },
  baja: { bg: '#FCEBEB', color: '#A32D2D' },
  permiso: { bg: '#F5EFD6', color: '#8B6914' },
  otro: { bg: '#f0f0ed', color: '#666' },
};
const ESTADO_COLOR = {
  pendiente: { bg: '#FFF8E6', color: '#B47B00' },
  aprobada: { bg: '#EAF3DE', color: '#3B6D11' },
  rechazada: { bg: '#FCEBEB', color: '#A32D2D' },
};

export default function Ausencias() {
  const { usuario } = useAuth();
  const isAdmin = usuario?.rol === 'admin';
  const navigate = useNavigate();
  const [ausencias, setAusencias] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useState('lista');
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [filtroEmpleado, setFiltroEmpleado] = useState('todos');
  const [modal, setModal] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [resumen, setResumen] = useState([]);
  const [form, setForm] = useState({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '', justificante_url: '', empleado_id: '' });
  const [error, setError] = useState('');
  const [mesCalendario, setMesCalendario] = useState(new Date());

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await API.get('/ausencias');
      setAusencias(res.data);
      if (isAdmin) {
        const empRes = await API.get('/empleados');
        setEmpleados(empRes.data);
        try {
          const resRes = await API.get('/ausencias/resumen/empleados');
          setResumen(resRes.data);
        } catch(e) {}
      }
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const calcularDias = (inicio, fin) => {
    if (!inicio || !fin) return 0;
    const d1 = new Date(inicio), d2 = new Date(fin);
    let dias = 0;
    const cur = new Date(d1);
    while (cur <= d2) {
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6) dias++;
      cur.setDate(cur.getDate() + 1);
    }
    return dias;
  };

  const guardar = async () => {
    setError('');
    if (!form.fecha_inicio || !form.fecha_fin) { setError('Las fechas son obligatorias'); return; }
    if (new Date(form.fecha_fin) < new Date(form.fecha_inicio)) { setError('La fecha fin debe ser posterior al inicio'); return; }
    if (isAdmin && !form.empleado_id) { setError('Selecciona un empleado'); return; }
    const dias = calcularDias(form.fecha_inicio, form.fecha_fin);
    try {
      const payload = {
        tipo: form.tipo,
        fecha_inicio: new Date(form.fecha_inicio).toISOString(),
        fecha_fin: new Date(form.fecha_fin).toISOString(),
        dias,
        motivo: form.motivo || null,
        empleado_id: isAdmin ? parseInt(form.empleado_id) : 1,
      };
      const res = await API.post('/ausencias', payload);
      if (form.justificante_url && res.data?.id) {
        await API.put('/ausencias/' + res.data.id, { justificante_url: form.justificante_url });
      }
      setModal(false);
      setForm({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '', justificante_url: '', empleado_id: '' });
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const aprobar = async (id, aprobado) => {
    await API.put('/ausencias/' + id + '/aprobar?aprobar=' + aprobado);
    if (detalle?.id === id) {
      const res = await API.get('/ausencias/' + id);
      setDetalle(res.data);
    }
    cargar();
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return;
    await API.delete('/ausencias/' + id);
    setDetalle(null);
    cargar();
  };

  const verDetalle = (id) => navigate('/ausencias/' + id);

  const ausenciasFiltradas = ausencias.filter(a => {
    if (filtroEstado !== 'todas' && a.estado !== filtroEstado) return false;
    if (filtroEmpleado !== 'todos' && a.empleado_id !== parseInt(filtroEmpleado)) return false;
    return true;
  });

  const diasDelMes = () => {
    const year = mesCalendario.getFullYear();
    const month = mesCalendario.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay: firstDay === 0 ? 6 : firstDay - 1, daysInMonth };
  };

  const ausenciasEnDia = (day) => {
    const year = mesCalendario.getFullYear();
    const month = mesCalendario.getMonth();
    const fecha = new Date(year, month, day);
    return ausenciasFiltradas.filter(a => {
      const ini = new Date(a.fecha_inicio);
      const fin = new Date(a.fecha_fin);
      return fecha >= ini && fecha <= fin;
    });
  };

  if (cargando) return <div style={s.loading}>Cargando...</div>;

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.topbarTitle}>Ausencias</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={vista === 'lista' ? s.btnActive : s.btnGhost} onClick={() => setVista('lista')}>Lista</button>
          <button style={vista === 'calendario' ? s.btnActive : s.btnGhost} onClick={() => setVista('calendario')}>Calendario</button>
          {isAdmin && <button style={vista === 'resumen' ? s.btnActive : s.btnGhost} onClick={() => setVista('resumen')}>Resumen</button>}
          <button style={s.btn} onClick={() => { setForm({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '', justificante_url: '', empleado_id: '' }); setError(''); setModal(true); }}>+ Nueva</button>
        </div>
      </div>

      <div style={s.content}>
        {vista !== 'resumen' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['todas', 'pendiente', 'aprobada', 'rechazada'].map(e => (
              <button key={e} onClick={() => setFiltroEstado(e)} style={filtroEstado === e ? s.filterActive : s.filter}>
                {e.charAt(0).toUpperCase() + e.slice(1)}
              </button>
            ))}
            {isAdmin && (
              <select style={s.select} value={filtroEmpleado} onChange={e => setFiltroEmpleado(e.target.value)}>
                <option value="todos">Todos los empleados</option>
                {empleados.map(emp => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
              </select>
            )}
          </div>
        )}

        {vista === 'lista' && (
          <div style={s.card}>
            {ausenciasFiltradas.length === 0 ? (
              <div style={s.empty}>No hay ausencias</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    {isAdmin && <th style={s.th}>Empleado</th>}
                    <th style={s.th}>Tipo</th>
                    <th style={s.th}>Desde</th>
                    <th style={s.th}>Hasta</th>
                    <th style={s.th}>Días</th>
                    <th style={s.th}>Estado</th>
                    {isAdmin && <th style={s.th}>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {ausenciasFiltradas.map(a => (
                    <tr key={a.id} style={s.tr} onClick={() => verDetalle(a.id)}>
                      {isAdmin && <td style={s.td}><span style={s.empNombre}>{empleados.find(e => e.id === a.empleado_id)?.nombre || '—'}</span></td>}
                      <td style={s.td}><span style={{ ...s.pill, background: TIPO_COLOR[a.tipo]?.bg || '#f0f0ed', color: TIPO_COLOR[a.tipo]?.color || '#666' }}>{a.tipo}</span></td>
                      <td style={s.td}>{new Date(a.fecha_inicio).toLocaleDateString('es-ES')}</td>
                      <td style={s.td}>{new Date(a.fecha_fin).toLocaleDateString('es-ES')}</td>
                      <td style={s.td}>{a.dias || '—'}</td>
                      <td style={s.td}><span style={{ ...s.pill, background: ESTADO_COLOR[a.estado]?.bg || '#f0f0ed', color: ESTADO_COLOR[a.estado]?.color || '#666' }}>{a.estado}</span></td>
                      {isAdmin && (
                        <td style={s.td} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {a.estado === 'pendiente' && <>
                              <button style={s.btnApprove} onClick={() => aprobar(a.id, true)}>✓</button>
                              <button style={s.btnReject} onClick={() => aprobar(a.id, false)}>✕</button>
                            </>}
                            <button style={s.btnDanger} onClick={() => eliminar(a.id)}>🗑</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {vista === 'calendario' && (
          <div style={s.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid #f0f0ed' }}>
              <button style={s.btnGhost} onClick={() => setMesCalendario(new Date(mesCalendario.getFullYear(), mesCalendario.getMonth() - 1, 1))}>←</button>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{mesCalendario.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}</div>
              <button style={s.btnGhost} onClick={() => setMesCalendario(new Date(mesCalendario.getFullYear(), mesCalendario.getMonth() + 1, 1))}>→</button>
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
                {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#aaa', padding: '4px 0', fontWeight: 500 }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {Array(diasDelMes().firstDay).fill(null).map((_, i) => <div key={'e'+i} />)}
                {Array(diasDelMes().daysInMonth).fill(null).map((_, i) => {
                  const day = i + 1;
                  const ausHoy = ausenciasEnDia(day);
                  return (
                    <div key={day} style={{ minHeight: 56, background: '#fafaf8', border: '0.5px solid #f0f0ed', borderRadius: 4, padding: 4 }}>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{day}</div>
                      {ausHoy.slice(0, 2).map(a => (
                        <div key={a.id} onClick={() => verDetalle(a.id)} style={{ fontSize: 9, background: TIPO_COLOR[a.tipo]?.bg || '#f0f0ed', color: TIPO_COLOR[a.tipo]?.color || '#666', padding: '1px 4px', borderRadius: 2, marginBottom: 1, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          {isAdmin ? (empleados.find(e => e.id === a.empleado_id)?.nombre?.split(' ')[0] || a.tipo) : a.tipo}
                        </div>
                      ))}
                      {ausHoy.length > 2 && <div style={{ fontSize: 9, color: '#aaa' }}>+{ausHoy.length - 2}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {vista === 'resumen' && isAdmin && (
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Empleado</th>
                  <th style={s.th}>Vacaciones</th>
                  <th style={s.th}>Bajas</th>
                  <th style={s.th}>Permisos</th>
                  <th style={s.th}>Otros</th>
                  <th style={s.th}>Total días</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map(r => (
                  <tr key={r.empleado_id} style={s.tr}>
                    <td style={s.td}><span style={s.empNombre}>{r.empleado_nombre}</span></td>
                    <td style={s.td}>{r.por_tipo?.vacaciones || 0}d</td>
                    <td style={s.td}>{r.por_tipo?.baja || 0}d</td>
                    <td style={s.td}>{r.por_tipo?.permiso || 0}d</td>
                    <td style={s.td}>{r.por_tipo?.otro || 0}d</td>
                    <td style={s.td}><strong>{r.total_dias}d</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div style={s.overlay} onClick={() => setModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>Nueva ausencia</div>
              <button style={s.closeBtn} onClick={() => setModal(false)}>✕</button>
            </div>
            {error && <div style={s.errorBox}>{error}</div>}
            {isAdmin && (
              <div style={s.field}>
                <label style={s.label}>Empleado *</label>
                <select style={s.input} value={form.empleado_id} onChange={e => setForm({ ...form, empleado_id: e.target.value })}>
                  <option value="">Selecciona empleado</option>
                  {empleados.map(emp => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
                </select>
              </div>
            )}
            <div style={s.field}>
              <label style={s.label}>Tipo *</label>
              <select style={s.input} value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={s.field}>
                <label style={s.label}>Fecha inicio *</label>
                <input style={s.input} type="date" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Fecha fin *</label>
                <input style={s.input} type="date" value={form.fecha_fin} onChange={e => setForm({ ...form, fecha_fin: e.target.value })} />
              </div>
            </div>
            {form.fecha_inicio && form.fecha_fin && (
              <div style={{ fontSize: 12, color: '#888', marginBottom: 12, marginTop: -8 }}>{calcularDias(form.fecha_inicio, form.fecha_fin)} días hábiles</div>
            )}
            <div style={s.field}>
              <label style={s.label}>Motivo</label>
              <input style={s.input} value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })} placeholder="Opcional" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Justificante (URL)</label>
              <input style={s.input} value={form.justificante_url} onChange={e => setForm({ ...form, justificante_url: e.target.value })} placeholder="https://..." />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Sube el fichero a Google Drive o similar y pega el enlace</div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setModal(false)}>Cancelar</button>
              <button style={s.btn} onClick={guardar}>Solicitar ausencia</button>
            </div>
          </div>
        </div>
      )}

      {detalle && (
        <div style={s.overlay} onClick={() => setDetalle(null)}>
          <div style={{ ...s.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>Detalle ausencia</div>
              <button style={s.closeBtn} onClick={() => setDetalle(null)}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Empleado', value: detalle.empleado_nombre },
                { label: 'Tipo', value: detalle.tipo },
                { label: 'Desde', value: new Date(detalle.fecha_inicio).toLocaleDateString('es-ES') },
                { label: 'Hasta', value: new Date(detalle.fecha_fin).toLocaleDateString('es-ES') },
                { label: 'Días hábiles', value: detalle.dias },
                { label: 'Estado', value: detalle.estado },
              ].map(item => (
                <div key={item.label} style={{ background: '#f5f5f3', borderRadius: 6, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>{item.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{item.value}</div>
                </div>
              ))}
            </div>
            {detalle.motivo && <div style={s.field}><label style={s.label}>Motivo</label><div style={{ fontSize: 14, color: '#444' }}>{detalle.motivo}</div></div>}
            {detalle.justificante_url && (
              <div style={s.field}>
                <label style={s.label}>Justificante</label>
                <a href={detalle.justificante_url} target="_blank" rel="noreferrer" style={{ color: '#378ADD', fontSize: 14 }}>Ver documento →</a>
              </div>
            )}
            {detalle.aprobada_por_nombre && <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Gestionado por: {detalle.aprobada_por_nombre}</div>}
            {isAdmin && detalle.estado === 'pendiente' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button style={{ ...s.btn, background: '#3B6D11', flex: 1 }} onClick={() => aprobar(detalle.id, true)}>✓ Aprobar</button>
                <button style={{ ...s.btn, background: '#A32D2D', flex: 1 }} onClick={() => aprobar(detalle.id, false)}>✕ Rechazar</button>
              </div>
            )}
            {detalle.historial?.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: '#aaa', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Historial</div>
                {detalle.historial.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, color: '#666', padding: '6px 0', borderBottom: '0.5px solid #f0f0ed' }}>
                    <span style={{ color: '#aaa' }}>{new Date(h.fecha).toLocaleDateString('es-ES')}</span>
                    <span style={{ fontWeight: 500 }}>{h.usuario}</span>
                    <span>{h.accion}{h.detalle ? ' — ' + h.detalle : ''}</span>
                  </div>
                ))}
              </div>
            )}
            {isAdmin && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button style={{ ...s.btnSecondary, color: '#A32D2D', borderColor: '#A32D2D' }} onClick={() => eliminar(detalle.id)}>Eliminar ausencia</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f5f5f3', fontFamily: '-apple-system, BlinkMacSystemFont, Inter, sans-serif' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#aaa', fontSize: 14 },
  topbar: { background: 'white', borderBottom: '0.5px solid #e0e0d8', padding: '0 24px', height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  topbarTitle: { fontSize: 14, fontWeight: 500, color: '#1a1a1a' },
  content: { padding: 24 },
  card: { background: 'white', border: '0.5px solid #e0e0d8', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 16px', fontSize: 11, fontWeight: 500, color: '#aaa', textAlign: 'left', borderBottom: '0.5px solid #f0f0ed', textTransform: 'uppercase', letterSpacing: '0.05em' },
  tr: { borderBottom: '0.5px solid #f0f0ed', cursor: 'pointer', background: 'white' },
  td: { padding: '12px 16px', fontSize: 13, color: '#1a1a1a', verticalAlign: 'middle' },
  pill: { fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 500 },
  empNombre: { fontWeight: 500 },
  btn: { background: '#378ADD', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnActive: { background: '#1a1a1a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnGhost: { background: 'white', color: '#666', border: '0.5px solid #d0d0c8', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  btnSecondary: { background: 'white', color: '#666', border: '0.5px solid #d0d0c8', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  btnApprove: { background: '#EAF3DE', color: '#3B6D11', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnReject: { background: '#FCEBEB', color: '#A32D2D', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnDanger: { background: '#f0f0ed', color: '#666', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 13, cursor: 'pointer' },
  filter: { background: 'white', color: '#666', border: '0.5px solid #d0d0c8', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer' },
  filterActive: { background: '#1a1a1a', color: 'white', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer' },
  select: { background: 'white', border: '0.5px solid #d0d0c8', borderRadius: 8, padding: '5px 12px', fontSize: 13, color: '#1a1a1a', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '48px', fontSize: 14, color: '#aaa' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: 12, border: '0.5px solid #e0e0d8', padding: 24, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  modalTitle: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#aaa' },
  errorBox: { background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, color: '#444', marginBottom: 6, fontWeight: 500 },
  input: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '0.5px solid #d0d0c8', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'white' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
};
